// client/src/components/learning/MidLesson.tsx
import React, { useState } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  max-width: 900px;
  margin: 40px auto;
  padding: 20px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  font-family: 'Roboto', sans-serif;
`;

const ModuleTitle = styled.h2`
  color: ${({ theme }) => theme.primary || '#7e22ce'};
  margin-bottom: 15px;
`;

const ModuleContent = styled.div`
  font-size: 1.1rem;
  line-height: 1.7;
  color: #333;
  margin-bottom: 20px;
`;

const Button = styled.button`
  margin: 10px 5px;
  padding: 10px 15px;
  background-color: ${({ theme }) => theme.primary || '#7e22ce'};
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.3s ease;
  &:hover {
    background-color: ${({ theme }) => theme.primaryDark || '#5a17a7'};
  }
`;

const OptionButton = styled(Button)`
  margin: 5px;
`;

const Input = styled.input`
  padding: 10px;
  font-size: 1rem;
  width: 100%;
  margin: 10px 0;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const NavContainer = styled.div`
  margin-top: 20px;
  text-align: center;
`;

const Feedback = styled.div<{ positive?: boolean }>`
  margin-top: 10px;
  font-weight: bold;
  color: ${({ positive }) => (positive ? 'green' : 'red')};
`;

enum ModuleType {
  Anatomy = 'Anatomy of the Heart',
  Function = 'Function of the Heart',
  HealthTips = 'Health Tips & Conditions',
  Quiz = 'Interactive Quiz',
  Simulation = 'Heart Simulation',
  Reflection = 'Mid-Lesson Reflection',
}

const modulesArr: ModuleType[] = [
  ModuleType.Anatomy,
  ModuleType.Function,
  ModuleType.HealthTips,
  ModuleType.Quiz,
  ModuleType.Simulation,
  ModuleType.Reflection,
];

interface MidLessonProps {
  onComplete: (data: any) => void;
}

const MidLesson: React.FC<MidLessonProps> = ({ onComplete }) => {
  const [currentModuleIndex, setCurrentModuleIndex] = useState<number>(0);
  const [selectedChamber, setSelectedChamber] = useState<string>('');
  const [chamberFeedback, setChamberFeedback] = useState<string>('');
  const [quizAnswer, setQuizAnswer] = useState<string>('');
  const [quizFeedback, setQuizFeedback] = useState<string>('');
  const [pumpCount, setPumpCount] = useState<number>(0);
  const [simFeedback, setSimFeedback] = useState<string>('');
  const [reflection, setReflection] = useState<string>('');

  const currentModule = modulesArr[currentModuleIndex];

  const nextModule = () => {
    if (currentModuleIndex < modulesArr.length - 1) {
      setCurrentModuleIndex(currentModuleIndex + 1);
    } else {
      // At the final module (Reflection), call onComplete with collected data.
      onComplete({
        reflection,
        selectedChamber,
        quizAnswer,
        pumpCount,
      });
    }
  };

  const prevModule = () => {
    if (currentModuleIndex > 0) {
      setCurrentModuleIndex(currentModuleIndex - 1);
    }
  };

  const renderModuleContent = () => {
    switch (currentModule) {
      case ModuleType.Anatomy:
        return (
          <>
            <ModuleTitle>Segment 1: Anatomy of the Heart</ModuleTitle>
            <ModuleContent>
              The heart has four chambers: Left Atrium, Right Atrium, Left Ventricle, and Right Ventricle.
              Click a chamber to learn its function.
            </ModuleContent>
            <div>
              {['Left Atrium', 'Right Atrium', 'Left Ventricle', 'Right Ventricle'].map(chamber => (
                <OptionButton
                  key={chamber}
                  onClick={() => {
                    setSelectedChamber(chamber);
                    setChamberFeedback(
                      `You selected ${chamber}. It ${chamber.includes('Atrium') ? 'receives blood' : 'pumps blood'}.`
                    );
                  }}
                >
                  {chamber}
                </OptionButton>
              ))}
            </div>
            {chamberFeedback && <Feedback positive>{chamberFeedback}</Feedback>}
          </>
        );
      case ModuleType.Function:
        return (
          <>
            <ModuleTitle>Segment 2: Function of the Heart</ModuleTitle>
            <ModuleContent>
              The heart pumps blood continuously to deliver oxygen and nutrients.
            </ModuleContent>
          </>
        );
      case ModuleType.HealthTips:
        return (
          <>
            <ModuleTitle>Segment 3: Health Tips & Conditions</ModuleTitle>
            <ModuleContent>
              Regular exercise and a balanced diet are key to maintaining a healthy heart.
            </ModuleContent>
          </>
        );
      case ModuleType.Quiz:
        return (
          <>
            <ModuleTitle>Segment 4: Interactive Quiz</ModuleTitle>
            <ModuleContent>
              Question: Which chamber receives oxygenated blood?
            </ModuleContent>
            <div>
              {['Left Atrium', 'Right Atrium', 'Left Ventricle', 'Right Ventricle'].map(option => (
                <OptionButton
                  key={option}
                  onClick={() => {
                    setQuizAnswer(option);
                    if (option === 'Left Atrium') {
                      setQuizFeedback('Correct! The left atrium receives oxygenated blood.');
                    } else {
                      setQuizFeedback('Incorrect. Please try again.');
                    }
                  }}
                >
                  {option}
                </OptionButton>
              ))}
            </div>
            {quizFeedback && (
              <Feedback positive={quizFeedback.startsWith('Correct')}>
                {quizFeedback}
              </Feedback>
            )}
          </>
        );
      case ModuleType.Simulation:
        return (
          <>
            <ModuleTitle>Segment 5: Heart Simulation</ModuleTitle>
            <ModuleContent>
              Click the button to simulate a heartbeat. Each click represents one heartbeat.
            </ModuleContent>
            <Button
              onClick={() => {
                const newCount = pumpCount + 1;
                setPumpCount(newCount);
                if (newCount >= 10) {
                  setSimFeedback('Great! The simulation shows a steady heartbeat.');
                } else {
                  setSimFeedback('');
                }
              }}
            >
              Pump Heart
            </Button>
            <ModuleContent>You have pumped {pumpCount} times.</ModuleContent>
            {simFeedback && <Feedback positive>{simFeedback}</Feedback>}
          </>
        );
      case ModuleType.Reflection:
        return (
          <>
            <ModuleTitle>Segment 6: Mid-Lesson Reflection</ModuleTitle>
            <ModuleContent>
              What did you learn about the heart? Please share your thoughts below:
            </ModuleContent>
            <Input
              type="text"
              value={reflection}
              onChange={(e) => setReflection(e.target.value)}
              placeholder="Your reflection..."
            />
          </>
        );
      default:
        return <div>Unsupported module type: {currentModule}</div>;
    }
  };

  return (
    <Container>
      {renderModuleContent()}
      <NavContainer>
        {currentModuleIndex > 0 && <Button onClick={prevModule}>Previous</Button>}
        <Button onClick={nextModule}>
          {currentModuleIndex === modulesArr.length - 1 ? 'Finish Mid Lesson' : 'Next'}
        </Button>
      </NavContainer>
    </Container>
  );
};

export default MidLesson;
